/**
 * usePersistedState — useState that survives navigation and page reload.
 *
 * Used to remember per-page UI inputs (active sub-tab, last-selected
 * strategy / watchlist / interval / filter values) so when the user
 * navigates away from a page and comes back, the page is in the same
 * state they left it.
 *
 * Storage:
 *   - localStorage under `${PERSIST_PREFIX}${key}` (versioned so we can
 *     invalidate the whole shared key-space in one shot)
 *   - JSON-serialised; non-serialisable values (functions, DOM nodes)
 *     should never be passed
 *
 * Quirks:
 *   - The setter signature matches React's: it accepts either a value
 *     or an updater function
 *   - On first mount, the persisted value (if any) wins over `initial`
 *   - If `key` changes at runtime (rare), state is re-read from storage
 *     for the new key on the next render
 *   - Errors during read/write are swallowed so a corrupt localStorage
 *     entry can't crash the page
 */
import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';

const PERSIST_PREFIX = 'pwa.state.v1.';

function read<T>(key: string, initial: T | (() => T)): T {
  try {
    const raw = localStorage.getItem(PERSIST_PREFIX + key);
    if (raw != null) return JSON.parse(raw) as T;
  } catch { /* corrupt entry — fall through to initial */ }
  return typeof initial === 'function' ? (initial as () => T)() : initial;
}

function write<T>(key: string, value: T): void {
  try {
    localStorage.setItem(PERSIST_PREFIX + key, JSON.stringify(value));
  } catch { /* quota / disabled — ignore */ }
}

export function usePersistedState<T>(
  key: string,
  initial: T | (() => T),
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => read(key, initial));
  // Re-sync if the caller's key changes between renders (rare; mostly
  // happens when a parent passes a dynamic key like ``tabs.${id}``).
  const lastKey = useRef(key);
  useEffect(() => {
    if (lastKey.current !== key) {
      lastKey.current = key;
      setValue(read(key, initial));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  // Write through to storage on every change.
  useEffect(() => {
    write(key, value);
  }, [key, value]);
  return [value, setValue];
}
